<script lang="ts">
	import type { VertiNodeLevelsProps } from "./VertiNodeLevelsProps.js";
	import Layout from "../components/Layout.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import arrowDown from "../../../../core/assets/icons/arrow-down.svg?raw";

	import {
		doesVertiNodeFeatureHaveDetail,
		getVertiNodeFeatureDetailPath,
	} from "../../../../core/schema/paths/getVertiNodeFeatureDetailPath.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./VertiNodeLevels.translations.json";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import type { VertiNodeFeature } from "../../../../core/schema/VertiNodeFeature.js";
	import VertiNodeLevelPicker from "../components/VertiNodeLevelPicker.svelte";
	import type { VertiNodeLevel } from "../../../../core/schema/VertiNodeLevel.js";
	import Icon from "../../../shared/components/Icon.svelte";
	import { contactPathMap } from "../../../../core/schema/paths/contactPathMap.js";
	import Link from "../../../shared/components/Link.svelte";
	import { querySizeMappings } from "../../../shared/components/Picture/querySizeMappings.js";

	type $$Props = VertiNodeLevelsProps;
	const { vertiNodeLevels, vertiNodeFeatures } = $$restProps as $$Props;
	const translate = getTranslate(translations);
	const { TENANT_LOCALE } = getGlobalSettings();

	let selectedOption: VertiNodeLevel["id"] | null = null;

	let expanded = new Set<VertiNodeFeature["id"]>();
	let windowWidth = 0;

	// Checking 0 windowWidth in case of prerender
	$: desktopSize = windowWidth >= querySizeMappings.md || windowWidth === 0;
	$: shownLevels =
		selectedOption === null || desktopSize
			? vertiNodeLevels
			: vertiNodeLevels.filter(({ id }) => id === selectedOption);
	$: shownFeatures = vertiNodeFeatures.filter(
		({ vertiNodeLevels }) =>
			shownLevels.some(({ id }) => vertiNodeLevels.includes(id)) &&
			(!selectedOption || vertiNodeLevels.includes(selectedOption) || desktopSize),
	);

	function toggleExpand(feature: VertiNodeFeature): void {
		if (expanded.has(feature.id)) {
			expanded.delete(feature.id);
		} else {
			expanded.add(feature.id);
		}
		expanded = expanded;
	}
</script>

<svelte:window bind:innerWidth={windowWidth} />

<Layout>
	<div class="container">
		<section class="mb-12 lg:mb-24">
			<h1 class="text-center">{translate("heading")}</h1>
			<p class="mx-auto max-w-xl text-center">{translate("text")}</p>

			<div class="mx-auto mb-8 w-max">
				<VertiNodeLevelPicker levels={vertiNodeLevels} bind:option={selectedOption} />
			</div>

			<div
				class="mx-auto grid max-w-3xl grid-cols-[1fr_auto_auto] gap-1 md:grid-cols-[max-content_repeat(var(--amount),1fr)_auto]"
				style="--amount: {shownLevels.length}"
			>
				<div class="col-span-full grid grid-cols-subgrid gap-2 rounded-lg bg-gray-300 p-4">
					<div class="min-w-xl font-spaceGrotesk text-base font-bold">{translate("headingFeatures")}</div>
					{#each shownLevels as level}
						<div class="font-spaceGrotesk text-center text-base font-bold">
							{level.name[TENANT_LOCALE]}
						</div>
					{/each}
				</div>
				{#each shownFeatures as feature}
					{@const isExpanded = expanded.has(feature.id)}

					<button
						class="col-span-full grid grid-cols-subgrid gap-x-2 rounded-lg bg-gray-300 p-4 text-left"
						on:click={() => {
							toggleExpand(feature);
						}}
					>
						<span class="min-w-xl font-spaceGrotesk text-primary self-center font-semibold leading-5">
							{feature.name[TENANT_LOCALE]}
						</span>
						{#each shownLevels as level}
							{@const isSelected = selectedOption === null || selectedOption === level.id}
							<span
								class="mx-auto block h-2 w-3 self-center"
								class:bg-primary={isSelected}
								class:bg-black={!isSelected}
								class:invisible={!feature.vertiNodeLevels.includes(level.id)}
							></span>
						{/each}
						<Icon
							icon={arrowDown}
							class="h-8 text-black transition-all duration-300 {isExpanded ? 'rotate-180' : ''}"
						/>
						<span
							class="pointer-events-none col-span-full grid overflow-clip transition-all duration-300 md:max-w-xs {isExpanded
								? 'grid-rows-[1fr]'
								: 'grid-rows-[0fr]'}"
						>
							<span class="text-2xs flex min-h-0 flex-col gap-2 transition-all duration-500" class:pt-3={isExpanded}>
								<span>{nbspify(feature.shortDescription[TENANT_LOCALE])}</span>
								{#if doesVertiNodeFeatureHaveDetail(feature)}
									<Link
										href="/{getVertiNodeFeatureDetailPath(feature)[TENANT_LOCALE]}"
										asButton
										variant="secondary"
										class="pointer-events-auto"
										on:click={(event) => {
											event.stopPropagation();
										}}
									>
										{translate("more")}
									</Link>
								{/if}
							</span>
						</span>
					</button>
				{/each}
			</div>

			<Link
				href="/{contactPathMap[TENANT_LOCALE]}#footerContact"
				asButton
				variant="primary"
				class="pointer-events-auto mx-auto mt-4"
				on:click={(event) => {
					event.stopPropagation();
				}}
			>
				{translate("interested")}
			</Link>
		</section>
	</div>
</Layout>
